import React, { useEffect, useRef, useState } from "react";
import PageShell from "./PageShell";
import "./PortalUI.css";
import {
    createUser,
    deleteUser,
    disableUser2FA,
    getPodcasts,
    getUserPodcastPermissions,
    getUsers,
    resendUserPasswordSetup,
    updateUser,
    updateUserPodcastPermissions,
} from "./api";

function normalizeName(user) {
    return [user?.first_name, user?.surname].filter(Boolean).join(" ") || user?.name || user?.email || user?.username || "Unknown";
}

function roleRank(role) {
    if (role === "admin") return 0;
    if (role === "client") return 1;
    return 2;
}

function formatTwoFactorLabel(user) {
    if (!user?.two_factor_enabled) return "Off";
    const method = String(user?.two_factor_method || "")
        .trim()
        .toLowerCase();
    if (method === "email") return "Email";
    return "App";
}

function defaultCapabilitiesForAccess(accessLevel) {
    const level = String(accessLevel || "").toLowerCase().trim();
    if (level === "write") {
        return {
            view_file_browser: true,
            episode_files_upload: true,
            episode_files_create_folder: false,
            episode_files_rename_delete: true,
            review_episodes: true,
            edit_episode_metadata: true,
            edit_show_notes: true,
            upload_media: true,
            send_notifications: true,
            view_change_history: true,
            create_episodes: false,
        };
    }
    if (level === "read_files") {
        return {
            view_file_browser: true,
            episode_files_upload: false,
            episode_files_create_folder: false,
            episode_files_rename_delete: false,
            review_episodes: true,
            edit_episode_metadata: false,
            edit_show_notes: false,
            upload_media: false,
            send_notifications: false,
            view_change_history: false,
            create_episodes: false,
        };
    }
    return {
        view_file_browser: false,
        episode_files_upload: false,
        episode_files_create_folder: false,
        episode_files_rename_delete: false,
        review_episodes: true,
        edit_episode_metadata: false,
        edit_show_notes: false,
        upload_media: false,
        send_notifications: false,
        view_change_history: false,
        create_episodes: false,
    };
}

const emptyForm = {
    id: null,
    first_name: "",
    surname: "",
    email: "",
    role: "client",
};

function UserManagement({ onLogout, embedded = false }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [query, setQuery] = useState("");

    const [podcasts, setPodcasts] = useState([]);
    const [loadingPodcasts, setLoadingPodcasts] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [mode, setMode] = useState("create");
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [modalError, setModalError] = useState("");
    const [podcastPermissions, setPodcastPermissions] = useState({});
    const [podcastPermissionCapabilities, setPodcastPermissionCapabilities] = useState({});
    const [loadingPermissions, setLoadingPermissions] = useState(false);
    const userFormRef = useRef(null);

    const sortedUsers = [...users].sort((a, b) => {
        const rd = roleRank(a.role) - roleRank(b.role);
        if (rd !== 0) return rd;
        return normalizeName(a).localeCompare(normalizeName(b), undefined, {
            sensitivity: "base",
        });
    });

    const visibleUsers = sortedUsers.filter((u) => {
        const q = String(query || "").trim().toLowerCase();
        if (!q) return true;
        const haystack = [normalizeName(u), u.email, u.role]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
        return haystack.includes(q);
    });

    async function loadUsers() {
        setLoading(true);
        setError("");
        try {
            const data = await getUsers();
            setUsers(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err.message || "Failed to load users");
        } finally {
            setLoading(false);
        }
    }

    async function loadPodcasts() {
        setLoadingPodcasts(true);
        try {
            const res = await getPodcasts();
            setPodcasts(Array.isArray(res?.podcasts) ? res.podcasts : []);
        } catch {
            setPodcasts([]);
        } finally {
            setLoadingPodcasts(false);
        }
    }

    useEffect(() => {
        loadUsers();
        loadPodcasts();
    }, []);

    const closeModal = () => {
        if (saving) return;
        setIsModalOpen(false);
        setModalError("");
        setLoadingPermissions(false);
    };

    useEffect(() => {
        const onKeyDown = (e) => {
            if (!isModalOpen) return;

            if (e.key === "Escape") {
                e.preventDefault();
                closeModal();
                return;
            }

            const isSaveShortcut =
                (e.key === "s" || e.key === "S") && (e.ctrlKey || e.metaKey);
            if (isSaveShortcut) {
                e.preventDefault();
                if (!saving && userFormRef.current) {
                    if (userFormRef.current.requestSubmit) {
                        userFormRef.current.requestSubmit();
                    } else {
                        userFormRef.current.submit();
                    }
                }
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [isModalOpen, saving]);

    const openCreate = () => {
        setMode("create");
        setForm({ ...emptyForm });
        setPodcastPermissions({});
        setPodcastPermissionCapabilities({});
        setModalError("");
        setIsModalOpen(true);
    };

    const openEdit = async (u) => {
        const mapped = {
            id: u.id,
            first_name: u.first_name || "",
            surname: u.surname || "",
            email: u.email || "",
            role: u.role || "client",
        };
        setMode("edit");
        setForm(mapped);
        setPodcastPermissions({});
        setPodcastPermissionCapabilities({});
        setModalError("");
        setIsModalOpen(true);

        if (mapped.role !== "client") return;

        setLoadingPermissions(true);
        try {
            const rows = await getUserPodcastPermissions(mapped.id);
            const next = {};
            const nextCaps = {};
            for (const row of rows) {
                const podcastId = Number(row.podcast_id);
                const access = String(row.access_level || "").toLowerCase();
                if (!podcastId) continue;
                if (!["read", "read_files", "write"].includes(access)) continue;
                next[podcastId] = access;
                nextCaps[podcastId] = {
                    ...defaultCapabilitiesForAccess(access),
                    ...(row?.capabilities || {}),
                };
            }
            setPodcastPermissions(next);
            setPodcastPermissionCapabilities(nextCaps);
        } catch (err) {
            setModalError(err.message || "Failed to load podcast permissions");
        } finally {
            setLoadingPermissions(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handlePermissionChange = (podcastId, accessLevel) => {
        const normalized = String(accessLevel || "").toLowerCase().trim();
        setPodcastPermissions((prev) => {
            const next = { ...prev };
            if (["read", "read_files", "write"].includes(normalized)) {
                next[podcastId] = normalized;
            } else {
                delete next[podcastId];
            }
            return next;
        });

        setPodcastPermissionCapabilities((prev) => {
            const next = { ...prev };
            if (["read", "read_files", "write"].includes(normalized)) {
                next[podcastId] = defaultCapabilitiesForAccess(normalized);
            } else {
                delete next[podcastId];
            }
            return next;
        });
    };

    const handleCapabilityChange = (podcastId, field, checked) => {
        setPodcastPermissionCapabilities((prev) => {
            const access = String(podcastPermissions[podcastId] || "").toLowerCase();
            if (!access) return prev;
            const current = prev[podcastId] || defaultCapabilitiesForAccess(access);
            return {
                ...prev,
                [podcastId]: {
                    ...current,
                    [field]: checked,
                },
            };
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setModalError("");
        try {
            let saved;
            const payload = {
                first_name: form.first_name,
                surname: form.surname,
                email: form.email,
                role: form.role,
            };
            if (form.id) {
                saved = await updateUser(form.id, payload);
            } else {
                saved = await createUser(payload);
            }

            const permissionsPayload = Object.entries(podcastPermissions).map(
                ([podcastId, accessLevel]) => ({
                    podcast_id: Number(podcastId),
                    access_level: accessLevel,
                    capabilities:
                        podcastPermissionCapabilities[podcastId] ||
                        defaultCapabilitiesForAccess(accessLevel),
                })
            );

            if (saved?.id && form.role === "client") {
                await updateUserPodcastPermissions(saved.id, permissionsPayload);
            } else if (saved?.id) {
                await updateUserPodcastPermissions(saved.id, []);
            }

            await loadUsers();
            closeModal();
        } catch (err) {
            setModalError(err.message || "Failed to save user");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!form.id) return;
        if (!window.confirm(`Delete user "${form.email || "this user"}"?`)) return;
        try {
            await deleteUser(form.id);
            await loadUsers();
            closeModal();
        } catch (err) {
            setModalError(err.message || "Failed to delete user");
        }
    };

    const handleDisable2FA = async (u) => {
        if (!window.confirm(`Disable 2FA for "${u.email || u.username}"?`)) return;
        try {
            await disableUser2FA(u.id);
            await loadUsers();
        } catch (err) {
            setError(err.message || "Failed to disable 2FA");
        }
    };

    const handleResendSetup = async (u) => {
        if (!window.confirm(`Resend password setup to "${u.email || u.username}"?`)) return;
        try {
            await resendUserPasswordSetup(u.id);
            setError("");
        } catch (err) {
            setError(err.message || "Failed to resend setup");
        }
    };

    return (
        <PageShell embedded={embedded} onLogout={onLogout}>
            <div className="portal-page">
                <div className="portal-page__header">
                    <div>
                        <h2 className="portal-page__title">User Management</h2>
                        <p className="portal-page__subtitle">
                            Manage admin and client users.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={openCreate}
                        className="btn btn--primary"
                    >
                        + Create User
                    </button>
                </div>

                <div className="card">
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                            marginBottom: 10,
                        }}
                    >
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            className="form-field__input"
                            placeholder="Search by name, email, role"
                            style={{ maxWidth: 420 }}
                        />
                    </div>

                    {error && <div className="alert alert--error">{error}</div>}
                    {loading && <p>Loading users…</p>}
                    {!loading && visibleUsers.length === 0 && <p>No users found.</p>}

                    {!loading && visibleUsers.length > 0 && (
                        <div style={{ overflowX: "auto" }}>
                            <table className="change-log-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Role</th>
                                        <th>2FA</th>
                                        <th>Created</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleUsers.map((u) => (
                                        <tr key={u.id}>
                                            <td>{normalizeName(u)}</td>
                                            <td>{u.email || "—"}</td>
                                            <td>{u.role}</td>
                                            <td>{formatTwoFactorLabel(u)}</td>
                                            <td>
                                                {u.created_at
                                                    ? new Date(u.created_at).toLocaleDateString()
                                                    : "—"}
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                                    <button
                                                        type="button"
                                                        className="btn btn--secondary btn--tiny"
                                                        onClick={() => openEdit(u)}
                                                    >
                                                        Edit
                                                    </button>
                                                    {u.two_factor_enabled ? (
                                                        <button
                                                            type="button"
                                                            className="btn btn--secondary btn--tiny"
                                                            onClick={() => handleDisable2FA(u)}
                                                        >
                                                            Disable 2FA
                                                        </button>
                                                    ) : null}
                                                    {!u.password_set_at ? (
                                                        <button
                                                            type="button"
                                                            className="btn btn--secondary btn--tiny"
                                                            onClick={() => handleResendSetup(u)}
                                                        >
                                                            Resend setup
                                                        </button>
                                                    ) : null}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal modal--wide">
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">
                                    {mode === "edit" ? "Edit User" : "Create User"}
                                </h3>
                                <p className="modal__subtitle">
                                    {mode === "edit"
                                        ? "Update user details and permissions."
                                        : "Create a new user account."}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="modal__close-btn"
                                onClick={closeModal}
                            >
                                ✕
                            </button>
                        </div>

                        {modalError && <div className="alert alert--error">{modalError}</div>}

                        <form
                            onSubmit={handleSave}
                            className="modal__body"
                            ref={userFormRef}
                        >
                            <fieldset className="fieldset">
                                <legend className="fieldset__legend">User Details</legend>
                                <label className="form-field">
                                    <span className="form-field__label">First name</span>
                                    <input
                                        className="form-field__input"
                                        name="first_name"
                                        value={form.first_name}
                                        onChange={handleChange}
                                        required
                                    />
                                </label>
                                <label className="form-field">
                                    <span className="form-field__label">Surname</span>
                                    <input
                                        className="form-field__input"
                                        name="surname"
                                        value={form.surname}
                                        onChange={handleChange}
                                        required
                                    />
                                </label>
                                <label className="form-field">
                                    <span className="form-field__label">Email</span>
                                    <input
                                        className="form-field__input"
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        required
                                    />
                                </label>
                                <label className="form-field">
                                    <span className="form-field__label">Role</span>
                                    <select
                                        className="form-field__select"
                                        name="role"
                                        value={form.role}
                                        onChange={handleChange}
                                    >
                                        <option value="admin">admin</option>
                                        <option value="client">client</option>
                                    </select>
                                </label>

                                {form.role === "client" && (
                                    <label className="form-field">
                                        <span className="form-field__label">
                                            Podcast Permissions
                                        </span>
                                        {loadingPermissions || loadingPodcasts ? (
                                            <p className="text-description">
                                                Loading podcasts and permissions…
                                            </p>
                                        ) : podcasts.length === 0 ? (
                                            <p className="text-description">
                                                No podcasts available yet.
                                            </p>
                                        ) : (
                                            <div className="podcast-permissions-list">
                                                {podcasts.map((p) => {
                                                    const current = podcastPermissions[p.id] || "";
                                                    const hasAccess = !!current;
                                                    const caps =
                                                        podcastPermissionCapabilities[p.id] ||
                                                        defaultCapabilitiesForAccess(current);
                                                    return (
                                                        <div
                                                            key={p.id}
                                                            className="podcast-permissions-row podcast-permissions-row--stack"
                                                        >
                                                            <div className="podcast-permissions-head">
                                                                <span className="podcast-permissions-name">
                                                                    {p.name}
                                                                </span>
                                                                <label className="form-field__checkbox-row">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={hasAccess}
                                                                        onChange={(e) =>
                                                                            handlePermissionChange(
                                                                                p.id,
                                                                                e.target.checked
                                                                                    ? "read"
                                                                                    : ""
                                                                            )
                                                                        }
                                                                    />
                                                                    <span className="form-field__label">
                                                                        Has access
                                                                    </span>
                                                                </label>
                                                            </div>
                                                            {hasAccess ? (
                                                                <div className="podcast-permissions-cap-grid">
                                                                    {[
                                                                        ["create_episodes", "Create episodes"],
                                                                        ["view_file_browser", "View file browser"],
                                                                        ["episode_files_upload", "Episode files: upload"],
                                                                        ["episode_files_create_folder", "Episodes: Create folders"],
                                                                        ["episode_files_rename_delete", "Episode files: rename/delete"],
                                                                        ["review_episodes", "Review episodes"],
                                                                        ["edit_episode_metadata", "Edit episode metadata"],
                                                                        ["edit_show_notes", "Edit show notes"],
                                                                        ["upload_media", "Upload media"],
                                                                        ["send_notifications", "Send notifications"],
                                                                        ["view_change_history", "View change history"],
                                                                    ].map(([key, label]) => (
                                                                        <label
                                                                            key={key}
                                                                            className="form-field__checkbox-row"
                                                                        >
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={!!caps[key]}
                                                                                onChange={(e) =>
                                                                                    handleCapabilityChange(
                                                                                        p.id,
                                                                                        key,
                                                                                        e.target.checked
                                                                                    )
                                                                                }
                                                                            />
                                                                            <span className="form-field__label">
                                                                                {label}
                                                                            </span>
                                                                        </label>
                                                                    ))}
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </label>
                                )}
                            </fieldset>

                            <div
                                className="modal__footer"
                                style={{
                                    justifyContent:
                                        mode === "edit" ? "space-between" : "flex-end",
                                }}
                            >
                                {mode === "edit" && (
                                    <div style={{ display: "flex", gap: 8 }}>
                                        <button
                                            type="button"
                                            className="btn btn--danger"
                                            onClick={handleDelete}
                                            disabled={saving}
                                        >
                                            Delete User
                                        </button>
                                    </div>
                                )}
                                <div
                                    style={{
                                        display: "flex",
                                        gap: 10,
                                        justifyContent: "flex-end",
                                    }}
                                >
                                    <button
                                        type="button"
                                        className="btn btn--secondary"
                                        onClick={closeModal}
                                        disabled={saving}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn--primary"
                                        disabled={saving}
                                    >
                                        {saving
                                            ? "Saving…"
                                            : mode === "edit"
                                                ? "Save Changes"
                                                : "Create"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </PageShell>
    );
}

export default UserManagement;
